import { functionsApi } from '../firebase';
import { PlatformConfig } from '../types';

let cachedConfig: PlatformConfig | null = null;
let configPromise: Promise<PlatformConfig> | null = null;

// Hardcoded fallback so map always works even if Firebase config cache is stale
const FALLBACK_MAPBOX_TOKEN = 'pk.eyJ1IjoiYmVkbWl0bWFwIiwiYSI6ImNtcmlkdXVhbzBqY2g0OG9hMDg0bzRuMG0ifQ.D_eSQPq11Z5H-56iaHpfpw';

/**
 * The full platform-config object with no server round-trip.
 *
 * Every value here is identical to the object produced when the
 * `getConfigStatus` callable failed, so a route that renders from this object
 * renders exactly what it rendered before on a config-fetch failure - without
 * importing the firebase/functions SDK to do it.
 */
export function buildFallbackConfig(): PlatformConfig {
  return {
    stripe: { payments: false, connect: false, identity: false },
    mapbox: true,
    mapboxToken: FALLBACK_MAPBOX_TOKEN,
    fcm: false,
    boostCost: 5,
    // SideQuests service fee: 18% of the Quest reward, added on top, with a
    // $5.00 minimum. Display value only — the server recalculates the fee.
    platformFeePercent: 18,
    platformFeeFixed: 0,
    minPayoutAmount: 10,
    supportEmail: 'support@trysidequests.com',
    // Minimum Quest reward for normal posters (approved admins are exempt).
    minimumJobPrice: 10,
    cancelPolicy: {
      beforeStartRefundPercent: 100,
      afterStartRefundPercent: 70,
      doerCancelPenalty: 15,
    },
  };
}

/**
 * Synchronous, SDK-free platform config.
 *
 * Returns the live server-backed config once it has been fetched, and the
 * local fallback before that. Callers that need a config to render use this so
 * that no route pays for the firebase/functions chunk merely to paint.
 */
export function getDefaultPlatformConfig(): PlatformConfig {
  return cachedConfig ?? buildFallbackConfig();
}

/**
 * Server-backed platform config (Stripe/Connect/Identity availability, FCM
 * readiness, boost cost, payout minimums). Loads the firebase/functions SDK on
 * first call, so only call it from a surface that genuinely needs live values.
 */
export async function getPlatformConfig(): Promise<PlatformConfig> {
  if (cachedConfig) return cachedConfig;
  if (configPromise) return configPromise;

  configPromise = new Promise(async (resolve) => {
    try {
      const { httpsCallable, functions } = await functionsApi();
      const fn = httpsCallable(functions, 'getConfigStatus');
      const resp = await fn({});
      const d = (resp.data as any) || {};
      const mapboxToken = FALLBACK_MAPBOX_TOKEN;
      cachedConfig = {
        stripe: d.stripe || { payments: false, connect: false, identity: false },
        mapbox: !!mapboxToken,
        mapboxToken,
        fcm: !!(d.fcm && d.fcm.configured),
        boostCost: d.boostCost || 5,
        // ⭐ PRICING v3: the poster pays 18% of the Quest reward (min $5.00)
        // ON TOP of the reward; the Quester keeps 100% of the advertised
        // reward. The server is authoritative for every amount.
        platformFeePercent: 18,
        platformFeeFixed: 0,
        minPayoutAmount: d.minPayoutAmount || 10,
        supportEmail: d.supportEmail || 'support@trysidequests.com',
        // Minimum Quest reward for normal posters (approved admins exempt).
        minimumJobPrice: 10,
        cancelPolicy: {
          beforeStartRefundPercent: 100,
          afterStartRefundPercent: 70,
          doerCancelPenalty: 15,
        },
      };
    } catch (e) {
      cachedConfig = buildFallbackConfig();
    }
    resolve(cachedConfig!);
    configPromise = null;
  });

  return configPromise;
}

export function getCachedConfig() {
  return cachedConfig;
}
